import { unwrapRelationItem } from './relationItems'
import { contactLabel, companyLabel } from './recordLabel'
import formatValue from './formatValue'
import { normalizeFieldKey } from './fieldKey'

// A relation value of a contact / company / media field, as a list or table cell shows it. A view's card or column points at one with
// `<field>.<relation field key>` — `client.contact`, `client.role` — where `client` is a field with relation_fields. addFieldType marks
// such a key `field_type: 'relation_value'` and gives it `relation_of` (the data key of the field: `client`, `contacts_persons`),
// `relation_key` and `relation_field` (the relation field's own definition). The row holds the field's links, each
// `{ attribute_id, value: record, relation_data: { <relation key>: value } }` (or the record with `relation_data` inside); the cell is
// that relation value of every link, in order, comma-separated.
// Imported by its own path (not through `shared/utils`, whose index pulls in the whole app).

export const RELATION_VALUE = 'relation_value'

const isObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v)

const isBlank = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)

const stripTags = (html) => String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

// One relation value as text, by the relation field's type: a contact or company by its name (a contact stored as a bare uid by its
// number, like any nameless contact), an option by its name, a date formatted, rich text without its markup, anything else as it is
export const relationValueLabel = (value, relationField) => {
  if (isBlank(value)) return ''
  if (Array.isArray(value)) return value.map(v => relationValueLabel(v, relationField)).filter(Boolean).join(', ')

  switch (relationField?.field_type) {
    case 'contact':
      // stored as a snapshot object, a bare uid, or just the name typed in ("Jenny")
      if (typeof value === 'string') return value
      return contactLabel(typeof value === 'number' ? { uid: value } : value)
    case 'company':
      return companyLabel(typeof value === 'number' ? { uid: value } : value)
    case 'option':
      return isObject(value) ? String(value.name ?? value.value ?? '') : String(value)
    case 'date':
      return String(formatValue(value, 'datetime|DD MMM yyy'))
    case 'datetime':
      return String(formatValue(value, 'datetime|DD MMM yyy HH:mm'))
    case 'richtext':
      return stripTags(value)
    default:
      return isObject(value) ? String(value.name ?? value.title ?? value.value ?? '') : String(value)
  }
}

const relationDataOf = (link) => link?.relation_data

// The cell for a `relation_value` field: the relation value of each of the row's links, joined
export const relationValueCell = (row, field) => {
  const value = row?.[field?.relation_of]
  const links = (Array.isArray(value) ? value : [value]).filter(Boolean).map(unwrapRelationItem)
  return links
    .map(link => relationValueLabel(relationDataOf(link)?.[field.relation_key] ?? relationDataOf(link)?.[normalizeFieldKey(field.relation_key)], field.relation_field))
    .filter(Boolean)
    .join(', ')
}
